import { tv } from 'tailwind-variants';
import { generateActions } from '../../state/actions';
import { type ContextMenuTarget, openContextMenu } from '../../state/contextMenu';
import type { PawnData } from '../../state/entities';
import {
  INVENTORY_SIZE,
  type ItemData,
  type ItemRarityType,
  selectedPawnInventory,
} from '../../state/pawnGear';
import { selectedIds } from '../../state/selection';

const inventoryPanel = tv({
  slots: {
    root: 'flex flex-col',
    title: [
      'text-center text-rs-gold font-semibold text-[0.8em] mb-[0.55em] tracking-wide',
      'border-b border-rs-stone-light/30 pb-[0.55em]',
    ],
    grid: ['grid grid-cols-4 gap-px', 'p-0'],
    footer: [
      'mt-[0.55em] pt-[0.55em]',
      'border-t border-rs-stone-light/30',
      'text-[0.68em] text-rs-parchment-dark text-center',
    ],
  },
});

const inventorySlot = tv({
  base: [
    'w-full aspect-square',
    'bg-gradient-to-b from-rs-stone-dark to-rs-stone-dark/80',
    'border border-rs-stone-light/30',
    'rounded-slot',
    'flex items-center justify-center',
    'transition-all duration-150',
    'cursor-pointer',
    'hover:border-rs-gold/50 hover:shadow-[0_0_6px_rgba(255,215,0,0.3)]',
    'relative',
    'overflow-hidden',
    'group',
  ],
  variants: {
    hasItem: {
      true: '',
      false: 'hover:bg-rs-stone-dark/60',
    },
    rarity: {
      common: 'border-rarity-common/50',
      uncommon: 'border-rarity-uncommon/50',
      rare: 'border-rarity-rare/50',
      epic: 'border-rarity-epic/50',
      legendary: 'border-rarity-legendary/50 shadow-[0_0_8px_rgba(255,128,0,0.3)]',
    },
  },
});

const itemDisplay = tv({
  base: ['w-full h-full', 'flex flex-col items-center justify-center', 'text-[0.68em] font-bold'],
  variants: {
    rarity: {
      common: 'text-rarity-common',
      uncommon: 'text-rarity-uncommon',
      rare: 'text-rarity-rare',
      epic: 'text-rarity-epic',
      legendary: 'text-rarity-legendary',
    },
  },
});

const itemTooltip = tv({
  base: [
    'absolute bottom-full left-1/2 -translate-x-1/2 mb-[0.28em]',
    'bg-black/95 text-rs-parchment',
    'px-[0.5em] py-[0.25em] rounded-[0.25em] text-[0.62em]',
    'opacity-0 group-hover:opacity-100',
    'transition-opacity duration-150',
    'pointer-events-none z-20',
    'whitespace-nowrap',
    'border border-rs-stone-light/30',
  ],
});

const quantityBadge = tv({
  base: [
    'absolute bottom-[0.12em] right-[0.12em]',
    'text-[0.56em] text-rs-gold font-bold',
    'bg-black/60 px-[0.18em] rounded',
  ],
});

interface InventorySlotProps {
  item: ItemData | null;
  index: number;
}

const handleInventoryContextMenu = ({
  event,
  item,
  slotIndex,
}: {
  event: MouseEvent;
  item: ItemData;
  slotIndex: number;
}): void => {
  event.preventDefault();
  event.stopPropagation();

  // Check if a pawn is selected
  const ids = Array.from(selectedIds.value);
  if (ids.length !== 1) {
    return;
  }

  const target: ContextMenuTarget = {
    type: 'inventoryItem',
    tileX: 0,
    tileY: 0,
    inventoryItem: {
      itemId: item.id,
      itemName: item.name,
      slotIndex,
    },
  };

  const actions = generateActions({ target });
  if (actions.length > 0) {
    openContextMenu({
      screenX: event.clientX,
      screenY: event.clientY,
      target,
      actions,
    });
  }
};

const InventorySlot = ({ item, index }: InventorySlotProps) => {
  const hasItem = item !== null;
  const rarity: ItemRarityType | undefined = hasItem ? item.rarity : undefined;
  const hasIcon = hasItem && item.icon && item.icon.length > 0;

  const onContextMenu = (event: MouseEvent) => {
    if (hasItem) {
      handleInventoryContextMenu({ event, item, slotIndex: index });
    }
  };

  return (
    <button
      type="button"
      class={inventorySlot({ hasItem, rarity: hasItem ? rarity : undefined })}
      title={item ? item.name : `Slot ${index + 1}`}
      onContextMenu={onContextMenu}
    >
      {hasItem && (
        <>
          <span class={itemTooltip()}>
            {item.name}
            {item.quantity > 1 && ` (${item.quantity})`}
          </span>
          <div class={itemDisplay({ rarity: item.rarity })}>
            {hasIcon ? (
              <img
                src={item.icon}
                alt={item.name}
                class="w-[94%] h-[94%] object-contain [image-rendering:pixelated]"
                draggable={false}
              />
            ) : (
              <span>{item.name.substring(0, 2).toUpperCase()}</span>
            )}
          </div>
          {item.quantity > 1 && (
            <span class={quantityBadge()}>
              {item.quantity >= 1000 ? `${Math.floor(item.quantity / 1000)}K` : item.quantity}
            </span>
          )}
        </>
      )}
    </button>
  );
};

interface InventoryPanelProps {
  pawn: PawnData;
}

export const InventoryPanel = ({ pawn: _pawn }: InventoryPanelProps) => {
  const inventory = selectedPawnInventory.value;
  const styles = inventoryPanel();

  if (!inventory) {
    return (
      <div class={styles.root()}>
        <div class={styles.title()}>Inventory</div>
        <div class="text-rs-parchment-dark text-[0.72em] text-center py-[2.2em]">
          No pawn selected
        </div>
      </div>
    );
  }

  const usedSlots = inventory.filter((item) => item !== null).length;

  return (
    <div class={styles.root()}>
      <div class={styles.title()}>Inventory</div>

      <div class={styles.grid()}>
        {inventory.map((item, index) => (
          <InventorySlot key={index} item={item} index={index} />
        ))}
      </div>

      <div class={styles.footer()}>
        {usedSlots}/{INVENTORY_SIZE} slots used
      </div>
    </div>
  );
};
